import { useEffect, useRef, useState } from 'react';
import type { PublicState } from '@karozik/shared';
import { Artwork, Avatar } from '@karozik/ui';
import type { WallSlide } from './usePhotoWall';

/** Where the slow zoom heads, in turn, so two photos never move alike. */
const ORIGINS = ['50% 50%', '30% 30%', '70% 65%', '65% 30%', '35% 70%'];

/**
 * A guest's photo across the whole screen: the photo itself blurred behind it,
 * who took it, the track playing and the QR code to add more. Fades out
 * between two photos (interlude mode) and when the wall stops.
 */
export function PhotoWall({ slide, state }: { slide: WallSlide | null; state: PublicState }) {
  // The photo on screen and the one before it, for the cross-fade.
  const [layers, setLayers] = useState<WallSlide[]>([]);
  const shown = useRef(false);
  useEffect(() => {
    if (slide) setLayers((l) => (shown.current ? [...l.slice(-1), slide] : [slide]));
    shown.current = !!slide;
  }, [slide]);

  const wall = state.staff?.photoWall;
  const top = layers[layers.length - 1];
  if (!top || !wall) return null;
  const np = state.nowPlaying;
  const token = state.event.joinUrl.split('/j/')[1];
  const author = wall.showAuthor ? top.photo.guest : null;

  return (
    <div className="absolute inset-0 overflow-hidden bg-night transition-opacity duration-700" style={{ opacity: slide ? 1 : 0 }} aria-hidden={!slide}>
      {layers.map((l) => (
        // Opaque, so the photo below never shows through once the fade is over.
        <div key={l.n} className="absolute inset-0 bg-night" style={{ animation: 'kz-fade-in 0.9s ease-out both' }}>
          <img src={l.photo.url} alt="" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-[6vh]" />
          <img
            src={l.photo.url}
            alt=""
            className="absolute inset-0 h-full w-full object-contain"
            style={{ animation: `kz-zoom ${wall.seconds + 1}s ease-out both`, transformOrigin: ORIGINS[l.n % ORIGINS.length] }}
          />
        </div>
      ))}
      <div className="absolute inset-x-0 bottom-0 h-[32vh] bg-gradient-to-t from-night/90 via-night/40 to-transparent" />

      <div key={top.n} className="animate-pop absolute bottom-[4vh] left-[3.5vw] flex max-w-[50vw] flex-col items-start gap-[1.4vh]">
        {top.fresh && <span className="bg-gradient-neon rounded-full px-[1.4vw] py-[0.8vh] text-[2.6vh] font-bold shadow-2xl">✨ Nouvelle photo !</span>}
        {author && (
          <div className="flex max-w-full items-center gap-[0.8vw] rounded-full py-[0.8vh] pr-[1.6vw] pl-[0.8vh] text-[3vh] whitespace-nowrap" style={{ background: `${author.color}55` }}>
            <Avatar guest={author} className="!h-[5.5vh] !w-[5.5vh] !text-[3.2vh]" />
            <span className="truncate">
              📸 <b>{author.pseudo}</b>
            </span>
          </div>
        )}
      </div>

      <div className="absolute right-[3.5vw] bottom-[4vh] flex items-end gap-[1.5vw]">
        {np && (
          <div className="glass flex max-w-[30vw] items-center gap-[1vw] rounded-[2vh] p-[1.2vh]">
            <Artwork track={np.track} className="h-[8vh] w-[8vh]" rounded="rounded-[1.2vh]" />
            <div className="min-w-0">
              <div className="truncate text-[2.4vh] font-semibold">{np.track.title}</div>
              <div className="truncate text-[2vh] text-ink-dim">{np.track.artist}</div>
            </div>
          </div>
        )}
        <div className="glass flex flex-col items-center gap-[0.6vh] rounded-[2vh] p-[1.2vh]">
          <img src={`/api/join/${token}/qr.svg`} alt="" className="h-[13vh] w-[13vh] rounded-[1vh] bg-white p-[0.5vh]" />
          <span className="text-[1.7vh] font-semibold text-ink-dim">Vos photos ici 📸</span>
        </div>
      </div>
    </div>
  );
}
